import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowRightIcon, ArrowLeftIcon } from "lucide-react";
import { Link } from "react-router-dom";

interface OTPVerificationFormProps {
  contactMethod: {
    type: "phone" | "email";
    value: string;
  };
  onVerify: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string;
}

export default function OTPVerificationForm({
  contactMethod,
  onVerify,
  onResend,
  onBack,
  isLoading = false,
  error,
}: OTPVerificationFormProps) {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  // Timer for resend functionality
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Handle OTP input
  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character
    setOtp(newOtp);

    // Auto-focus next input if current input is filled
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key press for backspace and delete
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      if (!otp[index] && index > 0) {
        // If current input is empty and backspace is pressed, focus previous input
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  // Handle paste functionality
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Check if pasted content is a valid OTP (numbers only)
    if (!/^\d+$/.test(pastedData)) return;

    const pastedOtp = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];

    // Fill the OTP fields with pasted data
    for (let i = 0; i < pastedOtp.length; i++) {
      if (i < 6) {
        newOtp[i] = pastedOtp[i];
      }
    }

    setOtp(newOtp);

    // Focus the next empty input or the last input if all are filled
    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  // Handle resend OTP
  const handleResend = () => {
    if (canResend) {
      onResend();
      setTimeLeft(60);
      setCanResend(false);
    }
  };

  // Handle verify OTP
  const handleVerify = () => {
    const otpString = otp.join("");
    if (otpString.length === 6) {
      onVerify(otpString);
    }
  };

  // Format phone number for display
  const formatContactValue = () => {
    if (contactMethod.type === "phone") {
      const phone = contactMethod.value;
      if (phone.length >= 4) {
        return `${phone.slice(0, -4)}****`;
      }
      return phone;
    }
    return contactMethod.value;
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">Verification Code</CardTitle>
        <CardDescription>
          We've sent a 6-digit code to your {contactMethod.type}{" "}
          <span className="font-medium">{formatContactValue()}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-12 text-center text-lg font-semibold"
              />
            ))}
          </div>

          {error && <p className="text-sm text-red-500 text-center">{error}</p>}

          <Button
            onClick={handleVerify}
            className="w-full"
            disabled={otp.join("").length !== 6 || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                <span>Verifying...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span>Verify Code</span>
                <ArrowRightIcon className="h-4 w-4" />
              </span>
            )}
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Didn't receive the code?
            </p>
            <Button
              variant="link"
              onClick={handleResend}
              disabled={!canResend || isLoading}
              className="p-0 h-auto"
            >
              {canResend ? "Resend Code" : `Resend code in ${timeLeft}s`}
            </Button>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={isLoading}
          className="flex items-center gap-1"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Back</span>
        </Button>
      </CardFooter>
    </Card>
  );
}
